/**
 * ════════════════════════════════════════════════════════════════════════════
 *  A MINIMAL QR ENCODER — BYTE MODE, ERROR CORRECTION LEVEL M, VERSIONS 1–10
 * ════════════════════════════════════════════════════════════════════════════
 *
 * ── WHY THIS IS HAND-WRITTEN ────────────────────────────────────────────────
 *
 * The 2FA enrolment screen has to draw an `otpauth://` URI as a QR code, and
 * there is no QR renderer this workspace may import: `antd`'s `QRCode` is
 * unreachable because importing `antd` directly is a lint error in the panels
 * (the kit in `@bookora/ui` is the only door, and it does not re-export it),
 * and `@rc-component/qrcode` is antd's transitive dependency rather than this
 * package's, so it does not resolve under pnpm's strict layout.
 *
 * The alternative was to ship enrolment with no QR at all. A URI a clinic
 * owner has to retype into Google Authenticator on a phone is how 2FA does not
 * get enabled, so the encoder is here — 250 lines, no dependencies, and the
 * screen ALSO shows the base32 secret for manual entry, so a failure to scan
 * is never a dead end.
 *
 * ── SCOPE, DELIBERATELY SMALL ───────────────────────────────────────────────
 *
 * Byte mode only (an `otpauth://` URI is mixed-case with punctuation, so no
 * other mode applies), level M, versions 1–10 → up to 213 bytes. A real URI is
 * around 120. Anything longer throws rather than guessing, and the caller
 * falls back to showing the secret.
 *
 * Reference: ISO/IEC 18004. The structure follows Project Nayuki's public
 * formulation, which is the clearest published statement of the placement and
 * masking rules.
 */

/** `[ecCodewordsPerBlock, group1Blocks, group1DataCodewords, group2Blocks, group2DataCodewords]` */
const VERSIONS_M: readonly (readonly [number, number, number, number, number])[] = [
  [10, 1, 16, 0, 0], // 1
  [16, 1, 28, 0, 0], // 2
  [26, 1, 44, 0, 0], // 3
  [18, 2, 32, 0, 0], // 4
  [24, 2, 43, 0, 0], // 5
  [16, 4, 27, 0, 0], // 6
  [18, 4, 31, 0, 0], // 7
  [22, 2, 38, 2, 39], // 8
  [22, 3, 36, 2, 37], // 9
  [26, 4, 43, 1, 44], // 10
];

const ALIGNMENT_CENTERS: readonly (readonly number[])[] = [
  [], // 1
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50], // 10
];

/* -------------------------------------------------------------------------- */
/* GF(256), primitive polynomial 0x11D                                        */
/* -------------------------------------------------------------------------- */

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);

{
  let x = 1;
  for (let i = 0; i < 255; i += 1) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i += 1) EXP[i] = EXP[i - 255];
}

function mul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[LOG[a] + LOG[b]];
}

/** g(x) = ∏ (x − α^i), coefficients highest-degree first. */
function generatorPoly(degree: number): number[] {
  let poly = [1];
  for (let i = 0; i < degree; i += 1) {
    const next = new Array<number>(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j += 1) {
      next[j] ^= poly[j];
      next[j + 1] ^= mul(poly[j], EXP[i]);
    }
    poly = next;
  }
  return poly;
}

function reedSolomon(data: readonly number[], ecLength: number): number[] {
  const gen = generatorPoly(ecLength);
  const buffer = new Array<number>(data.length + ecLength).fill(0);
  data.forEach((byte, index) => {
    buffer[index] = byte;
  });

  for (let i = 0; i < data.length; i += 1) {
    const coefficient = buffer[i];
    if (coefficient === 0) continue;
    for (let j = 0; j < gen.length; j += 1) {
      buffer[i + j] ^= mul(gen[j], coefficient);
    }
  }
  return buffer.slice(data.length);
}

/* -------------------------------------------------------------------------- */
/* Bit stream                                                                 */
/* -------------------------------------------------------------------------- */

function dataCodewordCount(version: number): number {
  const [, g1Blocks, g1Data, g2Blocks, g2Data] = VERSIONS_M[version - 1];
  return g1Blocks * g1Data + g2Blocks * g2Data;
}

/** Byte mode's character-count field is 8 bits up to version 9, 16 from 10. */
function countBits(version: number): number {
  return version < 10 ? 8 : 16;
}

function pickVersion(byteLength: number): number {
  for (let version = 1; version <= VERSIONS_M.length; version += 1) {
    const capacity = Math.floor((dataCodewordCount(version) * 8 - 4 - countBits(version)) / 8);
    if (byteLength <= capacity) return version;
  }
  throw new Error('QR_TOO_LONG');
}

function buildCodewords(bytes: Uint8Array, version: number): number[] {
  const total = dataCodewordCount(version);
  const bits: number[] = [];
  const push = (value: number, length: number): void => {
    for (let i = length - 1; i >= 0; i -= 1) bits.push((value >>> i) & 1);
  };

  push(0b0100, 4); // byte mode
  push(bytes.length, countBits(version));
  for (const byte of bytes) push(byte, 8);

  // Terminator, then pad to a byte boundary, then the two alternating pad
  // bytes the standard names.
  const capacityBits = total * 8;
  for (let i = 0; i < 4 && bits.length < capacityBits; i += 1) bits.push(0);
  while (bits.length % 8 !== 0) bits.push(0);

  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j += 1) byte = (byte << 1) | bits[i + j];
    codewords.push(byte);
  }
  for (let pad = 0xec; codewords.length < total; pad = pad === 0xec ? 0x11 : 0xec) {
    codewords.push(pad);
  }
  return codewords;
}

/** Splits into blocks, error-corrects each, and interleaves as the standard requires. */
function interleave(codewords: readonly number[], version: number): number[] {
  const [ecLength, g1Blocks, g1Data, g2Blocks, g2Data] = VERSIONS_M[version - 1];

  const blocks: number[][] = [];
  const ecBlocks: number[][] = [];
  let offset = 0;

  const take = (count: number, size: number): void => {
    for (let i = 0; i < count; i += 1) {
      const block = codewords.slice(offset, offset + size);
      offset += size;
      blocks.push(block);
      ecBlocks.push(reedSolomon(block, ecLength));
    }
  };
  take(g1Blocks, g1Data);
  take(g2Blocks, g2Data);

  const out: number[] = [];
  const maxData = Math.max(g1Data, g2Data);
  for (let i = 0; i < maxData; i += 1) {
    for (const block of blocks) if (i < block.length) out.push(block[i]);
  }
  for (let i = 0; i < ecLength; i += 1) {
    for (const block of ecBlocks) out.push(block[i]);
  }
  return out;
}

/* -------------------------------------------------------------------------- */
/* Matrix                                                                     */
/* -------------------------------------------------------------------------- */

type Grid = boolean[][];

const MASKS: readonly ((row: number, col: number) => boolean)[] = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

function blank(size: number): Grid {
  return Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
}

function drawFunctionPatterns(modules: Grid, reserved: Grid, version: number): void {
  const size = modules.length;
  const set = (row: number, col: number, dark: boolean): void => {
    if (row < 0 || col < 0 || row >= size || col >= size) return;
    modules[row][col] = dark;
    reserved[row][col] = true;
  };

  // Timing patterns.
  for (let i = 0; i < size; i += 1) {
    set(6, i, i % 2 === 0);
    set(i, 6, i % 2 === 0);
  }

  // Finder patterns, with their separators (the `dist === 4` ring).
  const finder = (centerRow: number, centerCol: number): void => {
    for (let dr = -4; dr <= 4; dr += 1) {
      for (let dc = -4; dc <= 4; dc += 1) {
        const dist = Math.max(Math.abs(dr), Math.abs(dc));
        set(centerRow + dr, centerCol + dc, dist !== 2 && dist !== 4);
      }
    }
  };
  finder(3, 3);
  finder(3, size - 4);
  finder(size - 4, 3);

  // Alignment patterns, skipping the three that would sit on a finder.
  const centers = ALIGNMENT_CENTERS[version - 1];
  for (let i = 0; i < centers.length; i += 1) {
    for (let j = 0; j < centers.length; j += 1) {
      const onFinder =
        (i === 0 && j === 0) ||
        (i === 0 && j === centers.length - 1) ||
        (i === centers.length - 1 && j === 0);
      if (onFinder) continue;
      const row = centers[i];
      const col = centers[j];
      for (let dr = -2; dr <= 2; dr += 1) {
        for (let dc = -2; dc <= 2; dc += 1) {
          set(row + dr, col + dc, Math.max(Math.abs(dr), Math.abs(dc)) !== 1);
        }
      }
    }
  }

  // Reserve the format-information strips; the values are written per mask.
  //
  // Index 6 is skipped in BOTH directions: (6, 8) and (8, 6) belong to the
  // timing patterns, not to the format field — which is exactly why the format
  // bits jump over them (`modules[8][14 - i]` for the last six). Reserving them
  // here would blank two timing modules and every scanner would lose the grid.
  for (let i = 0; i <= 8; i += 1) {
    if (i === 6) continue;
    set(8, i, false);
    set(i, 8, false);
  }
  for (let i = 0; i < 8; i += 1) {
    set(8, size - 1 - i, false);
    set(size - 1 - i, 8, false);
  }
  // The one module that is always dark.
  set(size - 8, 8, true);

  if (version >= 7) {
    let rem = version;
    for (let i = 0; i < 12; i += 1) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25);
    const bits = (version << 12) | rem;
    for (let i = 0; i < 18; i += 1) {
      const bit = ((bits >>> i) & 1) !== 0;
      const a = size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      set(b, a, bit);
      set(a, b, bit);
    }
  }
}

function drawFormatInfo(modules: Grid, mask: number): void {
  const size = modules.length;
  // Level M is `00`; the 10-bit BCH remainder and the 0x5412 mask are the
  // standard's, not a choice.
  const data = (0b00 << 3) | mask;
  let rem = data;
  for (let i = 0; i < 10; i += 1) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
  const bits = ((data << 10) | rem) ^ 0x5412;
  const bit = (index: number): boolean => ((bits >>> index) & 1) !== 0;

  for (let i = 0; i <= 5; i += 1) modules[i][8] = bit(i);
  modules[7][8] = bit(6);
  modules[8][8] = bit(7);
  modules[8][7] = bit(8);
  for (let i = 9; i < 15; i += 1) modules[8][14 - i] = bit(i);

  for (let i = 0; i < 8; i += 1) modules[8][size - 1 - i] = bit(i);
  for (let i = 8; i < 15; i += 1) modules[size - 15 + i][8] = bit(i);
  modules[size - 8][8] = true;
}

/** Two-column zigzag from the bottom-right, skipping the vertical timing column. */
function drawCodewords(modules: Grid, reserved: Grid, data: readonly number[], mask: number): void {
  const size = modules.length;
  const maskFn = MASKS[mask];
  let bitIndex = 0;
  let upward = true;

  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let vertical = 0; vertical < size; vertical += 1) {
      const row = upward ? size - 1 - vertical : vertical;
      for (let offset = 0; offset < 2; offset += 1) {
        const col = right - offset;
        if (reserved[row][col]) continue;

        let dark = false;
        if (bitIndex < data.length * 8) {
          const byte = data[bitIndex >>> 3];
          dark = ((byte >>> (7 - (bitIndex & 7))) & 1) !== 0;
          bitIndex += 1;
        }
        modules[row][col] = dark !== maskFn(row, col);
      }
    }
    upward = !upward;
  }
}

function penalty(modules: Grid): number {
  const size = modules.length;
  let score = 0;

  const scoreLine = (get: (index: number) => boolean): void => {
    let runColour = get(0);
    let runLength = 1;
    for (let i = 1; i < size; i += 1) {
      const value = get(i);
      if (value === runColour) {
        runLength += 1;
      } else {
        if (runLength >= 5) score += 3 + (runLength - 5);
        runColour = value;
        runLength = 1;
      }
    }
    if (runLength >= 5) score += 3 + (runLength - 5);
  };

  for (let i = 0; i < size; i += 1) {
    scoreLine((j) => modules[i][j]);
    scoreLine((j) => modules[j][i]);
  }

  for (let row = 0; row < size - 1; row += 1) {
    for (let col = 0; col < size - 1; col += 1) {
      const a = modules[row][col];
      if (
        a === modules[row][col + 1] &&
        a === modules[row + 1][col] &&
        a === modules[row + 1][col + 1]
      ) {
        score += 3;
      }
    }
  }

  let dark = 0;
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) if (modules[row][col]) dark += 1;
  }
  const percent = (dark * 100) / (size * size);
  score += Math.floor(Math.abs(percent - 50) / 5) * 10;

  return score;
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

export interface QrMatrix {
  size: number;
  /** `modules[row][col]` — `true` is a dark module. */
  modules: boolean[][];
  version: number;
  mask: number;
}

/**
 * Encodes `text` as a level-M QR matrix, choosing the smallest version that
 * fits and the mask with the lowest penalty score.
 *
 * Throws `QR_TOO_LONG` above 213 bytes; the caller should fall back to showing
 * the secret rather than rendering nothing.
 */
export function encodeQr(text: string): QrMatrix {
  const bytes = new TextEncoder().encode(text);
  const version = pickVersion(bytes.length);
  const size = version * 4 + 17;

  const codewords = interleave(buildCodewords(bytes, version), version);

  let best: QrMatrix | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (let mask = 0; mask < 8; mask += 1) {
    const modules = blank(size);
    const reserved = blank(size);
    drawFunctionPatterns(modules, reserved, version);
    drawCodewords(modules, reserved, codewords, mask);
    drawFormatInfo(modules, mask);

    const score = penalty(modules);
    if (score < bestScore) {
      bestScore = score;
      best = { size, modules, version, mask };
    }
  }

  if (!best) throw new Error('QR_ENCODE_FAILED');
  return best;
}

/**
 * Exposed for `qr-encode.test.ts` only.
 *
 * A QR code cannot be checked by eye and there is no decoder here, so the two
 * pieces of pure arithmetic that would break silently are asserted against the
 * standard's own published tables instead: the degree-10 generator polynomial
 * (Annex A) and the fifteen format-information strings for level M (Annex C).
 * If those two are right, a wrong QR is a placement bug, which the structural
 * assertions in the same file cover.
 */
export const qrInternals = {
  /** Generator polynomial coefficients as α exponents, highest degree first. */
  generatorExponents(degree: number): number[] {
    return generatorPoly(degree).map((coefficient) => LOG[coefficient]);
  },
  /**
   * Evaluates a GF(256) polynomial (highest degree first) at α^exponent.
   *
   * This is what lets the test check the two identities that define a correct
   * encoder without a table to mistype: the generator polynomial has α^0…α^(n−1)
   * as roots, and a Reed–Solomon codeword evaluates to zero at each of them.
   */
  evaluate(poly: readonly number[], exponent: number): number {
    let acc = 0;
    for (const coefficient of poly) {
      acc = mul(acc, EXP[exponent % 255]) ^ coefficient;
    }
    return acc;
  },
  generator(degree: number): number[] {
    return generatorPoly(degree);
  },
  /** `[...data, ...ec]` — a full Reed–Solomon codeword. */
  codeword(data: readonly number[], ecLength: number): number[] {
    return [...data, ...reedSolomon(data, ecLength)];
  },
  /** The 15 format bits for error-correction level M and the given mask. */
  formatBits(mask: number): string {
    const data = (0b00 << 3) | mask;
    let rem = data;
    for (let i = 0; i < 10; i += 1) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    const bits = ((data << 10) | rem) ^ 0x5412;
    return bits.toString(2).padStart(15, '0');
  },
};
